import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import {
  useGetTemplates,
  useGetItems,
  useGetItemsConstraints,
} from "../../../hooks/queries/useSpecQueries";
import {
  useSpecificationStateStore,
  getItemMetadata,
  isItem,
  type ChangeTarget,
  type Change,
} from "./specificationState";
import { parseBomPath } from "../../utils/conditions";
import {
  bomChildItemUuids,
  buildChildrenTableRows,
  DUPLICATE_CHILD_MESSAGE,
} from "../../utils/bomSlots";
import {
  useValidationStateStore,
  getAllowedBomTemplateUuids,
  getRequiredBomItemTypeConstraints,
  getRequiredBomTemplateConstraints,
  getAggregateLimitConstraints,
  buildAttributeUuidTranslator,
  buildFormulaDependencyMap,
  buildChildCountMaps,
  canDeleteBomItem,
  runClientSideValidations,
} from "./validationState";
import {
  Section,
  SectionHeader,
  NoDataText,
  AddTableRow,
  AddButton,
} from "../../components/SectionComponents";
import { ChildRow } from "./ChildRow";
import { SlotPlaceholderRow } from "./SlotPlaceholderRow";
import { TableCell, TableHeadCell } from "../../components/TableCell";

const ChildrenTableContainer = styled(TableContainer)(({ theme }) => ({
  border: `1px solid #e0e0e0`,
  borderRadius: "4px",
  overflow: "hidden",
}));

const NewChildRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "rgba(25, 118, 210, 0.08)",
}));

type ChildrenSectionProps = {
  item: ArenaOnlyItem | Item;
  filteredChildren: Array<{ child: BOMItem; originalIndex: number }>;
  onItemClick: (item: ItemWithBOM) => void;
  onPartNumberReplace: (child: ItemWithBOM, bomIndex: number) => void;
  sectionRef?: React.RefObject<HTMLDivElement>;
  isParentNew?: boolean;
  readOnly?: boolean;
  // Whether an empty BOM line slot stand-in matches the active search.
  slotMatchesSearch?: (slot: BomSlot) => boolean;
};

const ChildrenSection = ({
  item,
  filteredChildren,
  onItemClick,
  onPartNumberReplace,
  sectionRef,
  isParentNew = false,
  readOnly = false,
  slotMatchesSearch,
}: ChildrenSectionProps) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [pendingItemUuids, setPendingItemUuids] = useState<string[]>([]);
  // Pending slot fills: added item uuid -> the slot it was added through.
  // Keyed by item uuid; an item already in the BOM (or pending) cannot be
  // added again, so two fills never share a key.
  const [pendingSlotFills, setPendingSlotFills] = useState<
    Record<string, BomSlot>
  >({});
  // Line of the stand-in whose template picker is open.
  const [fillingSlotLine, setFillingSlotLine] = useState<number | null>(null);
  const [searchParams] = useSearchParams();

  const { uuid, partNumber } = getItemMetadata(item);
  const constraints = useValidationStateStore((state) => state.constraints);
  const condition = useSpecificationStateStore((state) => state.condition);
  const trackChange = useSpecificationStateStore((state) => state.trackChange);
  const removeChange = useSpecificationStateStore(
    (state) => state.removeChange
  );
  const appendBomItem = useSpecificationStateStore(
    (state) => state.appendBomItem
  );
  const getChange = useSpecificationStateStore((state) => state.getChange);
  const setConstraints = useValidationStateStore(
    (state) => state.setConstraints
  );
  const setAttributeUuidTranslator = useValidationStateStore(
    (state) => state.setAttributeUuidTranslator
  );
  const setFormulaDependencies = useValidationStateStore(
    (state) => state.setFormulaDependencies
  );

  // Subscribe to violations reactively so warning icons update immediately
  // Derive currentPath from URL params
  const currentPath = searchParams.get("bom-item") || "";

  // Use the hook to fetch pending items
  const { items: fetchedItems, isLoading: isFetchingItems } =
    useGetItems(pendingItemUuids);

  // Fetch constraints for pending items
  const {
    constraints: pendingItemConstraints,
    isLoading: isLoadingPendingConstraints,
  } = useGetItemsConstraints(pendingItemUuids);

  // When items AND constraints are fetched, append items to BOM, merge constraints, then clear pending
  useEffect(() => {
    if (
      pendingItemUuids.length > 0 &&
      !isFetchingItems &&
      !isLoadingPendingConstraints &&
      fetchedItems.length > 0 &&
      pendingItemConstraints.length > 0
    ) {
      fetchedItems.forEach((fetchedItem) => {
        const slot = isItem(fetchedItem.item)
          ? pendingSlotFills[fetchedItem.item.uuid]
          : undefined;
        const bomItem: BOMItem = {
          ...fetchedItem,
          quantity: 1,
          // Added through a slot stand-in: send that slot's line as its entry.
          ...(slot && {
            line_number: slot.line_number,
            hint: slot.hint,
            slot_entry: true,
          }),
        };
        appendBomItem(currentPath, bomItem);
      });

      // Merge all pending item constraints with the existing parent constraints
      const mergedConstraints: ConstraintsResponse = {
        item_constraints: [...(constraints?.item_constraints || [])],
        process_constraints: [...(constraints?.process_constraints || [])],
      };

      pendingItemConstraints.forEach((itemConstraint) => {
        mergedConstraints.item_constraints.push(
          ...(itemConstraint.item_constraints || [])
        );
        mergedConstraints.process_constraints.push(
          ...(itemConstraint.process_constraints || [])
        );
      });

      setConstraints(mergedConstraints);

      // Rebuild attribute UUID translator and formula dependencies
      if (condition) {
        const translator = buildAttributeUuidTranslator(condition);
        setAttributeUuidTranslator(translator);

        const formulaDeps = buildFormulaDependencyMap(
          mergedConstraints,
          translator
        );
        setFormulaDependencies(formulaDeps);
      }

      // Clear pending items only after all operations are complete
      setPendingItemUuids([]);
      setPendingSlotFills({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedItems,
    isFetchingItems,
    isLoadingPendingConstraints,
    pendingItemUuids,
    pendingItemConstraints,
    pendingSlotFills,
    appendBomItem,
    currentPath,
    condition,
    constraints,
    setConstraints,
    setAttributeUuidTranslator,
    setFormulaDependencies,
  ]);

  // Reset isAddingNew when the item changes
  useEffect(() => {
    setIsAddingNew(false);
    setFillingSlotLine(null);
  }, [uuid, partNumber]);

  // Helper to navigate to current item using path
  const getCurrentItemFromPath = useMemo(() => {
    if (!condition) return null;

    const indices = parseBomPath(currentPath);

    // Navigate to the item at the path
    let currentItem: ItemWithBOM = condition;
    for (const index of indices) {
      if (index >= currentItem.bom.length) return null;
      currentItem = currentItem.bom[index];
    }

    return currentItem;
  }, [condition, currentPath]);

  // Get BOM changes for the current item
  const bomChanges = useMemo(() => {
    return getCurrentItemFromPath?.bom_changes || [];
  }, [getCurrentItemFromPath]);

  // Just use filtered children - no need to track pending items separately

  const handleDelete = (bomIndex: number) => {
    // Find the filtered child entry by its original index
    const childEntry = filteredChildren.find(
      (entry) => entry.originalIndex === bomIndex
    );
    if (!childEntry || !isItem(childEntry.child.item)) return;

    const changeTarget: ChangeTarget = {
      type: "bom",
      path: currentPath,
      bomIndex: bomIndex,
      addedItemUuid: childEntry.child.item.uuid,
    };

    const change: Change = {
      target: changeTarget,
      operation: "delete",
      currentValue: undefined,
      previousValue: undefined,
      timestamp: Date.now(),
    };

    trackChange(change);
  };

  const handleUndo = (bomIndex: number) => {
    // Find the filtered child entry by its original index
    const childEntry = filteredChildren.find(
      (entry) => entry.originalIndex === bomIndex
    );
    if (!childEntry || !isItem(childEntry.child.item)) return;

    const changeTarget: ChangeTarget = {
      type: "bom",
      path: currentPath,
      bomIndex: bomIndex,
      addedItemUuid: childEntry.child.item.uuid,
    };

    removeChange(changeTarget);
  };

  const isBomItemDeleted = useCallback(
    (bomIndex: number): boolean => {
      return bomChanges.some(
        (change) => change.is_deleted && change.index === bomIndex
      );
    },
    [bomChanges]
  );

  const isBomItemAdded = useCallback(
    (bomIndex: number): boolean => {
      // If parent is new, all children appear as new
      if (isParentNew) {
        return true;
      }
      return bomChanges.some(
        (change) => change.is_added && change.index === bomIndex
      );
    },
    [bomChanges, isParentNew]
  );

  const handleAddClick = () => {
    setFillingSlotLine(null);
    setIsAddingNew(true);
  };

  // Re-run validations when condition/constraints change (e.g. after
  // additions/deletions or newly-fetched constraints).  Individual
  // trackChange calls already trigger runClientSideValidations, but this
  // effect catches non-trackChange-driven changes such as constraint updates.
  useEffect(() => {
    if (!condition || !constraints) return;

    runClientSideValidations({
      condition,
      constraints,
      path: currentPath,
      getChange,
    });
  }, [condition, constraints, currentPath, bomChanges, getChange]);

  const handleTemplateSelect = (templateUuid: string, slot?: BomSlot) => {
    const template = templates.find((t) => t.uuid === templateUuid);

    if (!template?.base_item_uuid || takenTemplateUuids.has(template.uuid)) {
      return;
    }

    const itemUuid = template.base_item_uuid;

    // The new BOM item will be added at the end of the entire BOM (not just filtered)
    const currentItemFromPath = getCurrentItemFromPath;
    const newBomIndex = currentItemFromPath?.bom?.length || 0;

    const changeTarget: ChangeTarget = {
      type: "bom",
      path: currentPath,
      bomIndex: newBomIndex,
      addedItemUuid: itemUuid,
    };

    const change: Change = {
      target: changeTarget,
      operation: "add",
      currentValue: undefined,
      previousValue: undefined,
      timestamp: Date.now(),
    };

    // Track the change
    trackChange(change);

    // Add to pending items to trigger fetch via useGetItems hook
    setPendingItemUuids((prev) => [...prev, itemUuid]);
    if (slot) {
      setPendingSlotFills((prev) => ({ ...prev, [itemUuid]: slot }));
    }

    setIsAddingNew(false);
    setFillingSlotLine(null);
  };

  // Count current children by template UUID and item type UUID (excluding deleted items)
  const { childCountsByTemplate, childCountsByItemType } = useMemo(() => {
    if (!getCurrentItemFromPath?.bom) {
      return {
        childCountsByTemplate: new Map<string, number>(),
        childCountsByItemType: new Map<string, number>(),
      };
    }

    return buildChildCountMaps(
      getCurrentItemFromPath.bom,
      currentPath,
      isBomItemDeleted,
      getChange
    );
  }, [getCurrentItemFromPath, isBomItemDeleted, getChange, currentPath]);

  // Count current children by identifier (for aggregate limit constraints)
  // Identifiers can be: base template UUID, item type UUID, or item type name string
  const childCountsByIdentifier = useMemo(() => {
    const counts = new Map<string, number>();
    filteredChildren.forEach(({ child, originalIndex }) => {
      if (isItem(child.item) && !isBomItemDeleted(originalIndex)) {
        const identifiers: string[] = [];

        // Add template UUID if available
        const templateUuid = child.item.base_template?.template_definition_uuid;
        if (templateUuid) {
          identifiers.push(templateUuid);
        }

        // Add item type UUID if available
        const itemTypeUuid = child.item.item_type?.uuid;
        if (itemTypeUuid) {
          identifiers.push(itemTypeUuid);
        }

        // Add item type name if available
        const itemTypeName = child.item.item_type?.name;
        if (itemTypeName) {
          identifiers.push(itemTypeName);
        }

        // Increment count for each identifier
        identifiers.forEach((identifier) => {
          counts.set(
            identifier,
            (counts.get(identifier) || 0) + child.quantity
          );
        });
      }
    });
    return counts;
  }, [filteredChildren, isBomItemDeleted]);

  // Note: We don't validate on mount/render because it causes issues with
  // violations being cleared by re-renders with stale data.
  // Validation only happens on blur when the user changes a quantity.

  // Check if a BOM item can be deleted without violating min_quantity constraints
  const canDeleteBomItemAtIndex = useCallback(
    (bomIndex: number): { canDelete: boolean; reason?: string } => {
      if (!uuid || !constraints) return { canDelete: true };

      const childEntry = filteredChildren.find(
        (entry) => entry.originalIndex === bomIndex
      );
      if (!childEntry || !isItem(childEntry.child.item)) {
        return { canDelete: true };
      }

      const requiredBomTemplateConstraints = getRequiredBomTemplateConstraints(
        constraints,
        uuid
      );
      const requiredBomItemTypeConstraints = getRequiredBomItemTypeConstraints(
        constraints,
        uuid
      );

      return canDeleteBomItem(
        childEntry.child,
        requiredBomTemplateConstraints,
        requiredBomItemTypeConstraints,
        childCountsByTemplate,
        childCountsByItemType
      );
    },
    [
      uuid,
      constraints,
      filteredChildren,
      childCountsByTemplate,
      childCountsByItemType,
    ]
  );

  // Get allowed template UUIDs and filter by RequiredBOMTemplateConstraint before fetching
  // This optimization reduces API calls by filtering out templates at max_quantity before fetching template details
  const filteredTemplateUuids = useMemo(() => {
    const allowedUuids = getAllowedBomTemplateUuids(constraints, uuid || "");
    if (!uuid) return [];

    const requiredBomTemplateConstraints = getRequiredBomTemplateConstraints(
      constraints,
      uuid
    );

    // Filter out templates that have reached max_quantity
    const filtered = allowedUuids.filter((templateUuid) => {
      const templateConstraint = requiredBomTemplateConstraints.find(
        (c: any) => c.target_template_uuid === templateUuid
      );
      if (templateConstraint) {
        const maxQty = (templateConstraint as any).config?.max_quantity;
        if (maxQty !== undefined && maxQty !== null) {
          const currentCount = childCountsByTemplate.get(templateUuid) || 0;
          if (currentCount >= maxQty) return false;
        }
      }
      return true;
    });

    return filtered;
  }, [constraints, uuid, childCountsByTemplate]);

  // Fetch only the filtered templates
  const { templates, isLoading: isLoadingTemplates } = useGetTemplates(
    filteredTemplateUuids
  );

  // Get base item UUIDs from templates to fetch their item types
  const baseItemUuids = useMemo(() => {
    return templates
      .map((t) => t.base_item_uuid)
      .filter((uuid): uuid is string => !!uuid);
  }, [templates]);

  // Fetch base items to get their item types
  const { items: baseItems } = useGetItems(baseItemUuids);

  // Create a map of template UUID to item type UUID
  const templateToItemTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    templates.forEach((template) => {
      if (template.base_item_uuid) {
        const baseItemWithBom = baseItems.find(
          (itemWithBom) =>
            isItem(itemWithBom.item) &&
            itemWithBom.item.uuid === template.base_item_uuid
        );
        if (baseItemWithBom && isItem(baseItemWithBom.item)) {
          map.set(template.uuid, baseItemWithBom.item.item_type.uuid);
        }
      }
    });
    return map;
  }, [templates, baseItems]);

  // Get available templates (filter out those already in children or at max item type quantity)
  const availableTemplates = useMemo(() => {
    if (!uuid) return [];

    const requiredBomItemTypeConstraints = getRequiredBomItemTypeConstraints(
      constraints,
      uuid
    );
    const aggregateLimitConstraints = getAggregateLimitConstraints(
      constraints,
      uuid
    );

    return templates.filter((template) => {
      if (!template.base_item_uuid) return false;

      // Check RequiredBOMItemTypeConstraint max_quantity
      const templateItemTypeUuid = templateToItemTypeMap.get(template.uuid);
      if (templateItemTypeUuid) {
        const itemTypeConstraint = requiredBomItemTypeConstraints.find(
          (c: any) => c.target_item_type_uuid === templateItemTypeUuid
        );
        if (itemTypeConstraint) {
          const maxQty = (itemTypeConstraint as any).config?.max_quantity;
          if (maxQty !== undefined && maxQty !== null) {
            const currentCount =
              childCountsByItemType.get(templateItemTypeUuid) || 0;
            if (currentCount >= maxQty) return false;
          }
        }
      }

      // Check AggregateLimitConstraint max_total
      // AggregateLimitConstraint groups multiple items together by field_identifiers
      // (which can be template UUIDs, item type UUIDs, or item type name strings)
      // and enforces a max_total across all of them.
      // Example: If constraint says ["TemplateA", "TemplateB"] with max_total=5,
      // then the total quantity of items from TemplateA + TemplateB cannot exceed 5.

      // Get all identifiers for this template
      const templateIdentifiers: string[] = [template.uuid];
      if (templateItemTypeUuid) {
        templateIdentifiers.push(templateItemTypeUuid);

        // Also add item type name from the base item
        const baseItemWithBom = baseItems.find(
          (itemWithBom) =>
            isItem(itemWithBom.item) &&
            itemWithBom.item.uuid === template.base_item_uuid
        );
        if (baseItemWithBom && isItem(baseItemWithBom.item)) {
          const itemTypeName = baseItemWithBom.item.item_type?.name;
          if (itemTypeName) {
            templateIdentifiers.push(itemTypeName);
          }
        }
      }

      // Check if any aggregate limit constraint would be violated
      for (const constraint of aggregateLimitConstraints) {
        const config = (constraint as any).config;
        const fieldIdentifiers = config?.field_identifiers || [];
        const maxTotal = config?.max_total;

        if (maxTotal !== undefined && maxTotal !== null) {
          // Check if this template's identifiers overlap with constraint's field_identifiers
          const hasOverlap = templateIdentifiers.some((id) =>
            fieldIdentifiers.includes(id)
          );

          if (hasOverlap) {
            // Calculate current total for all identifiers in this constraint
            let currentTotal = 0;
            fieldIdentifiers.forEach((identifier: string) => {
              currentTotal += childCountsByIdentifier.get(identifier) || 0;
            });

            // If we're already at or above max_total, filter out this template
            if (currentTotal >= maxTotal) {
              return false;
            }
          }
        }
      }

      return true;
    });
  }, [
    templates,
    uuid,
    constraints,
    childCountsByItemType,
    childCountsByIdentifier,
    templateToItemTypeMap,
    baseItems,
  ]);

  // While the BOM or processes have unsaved changes, show the lines FormSpec
  // will assign on save (added children may take free slots of their type,
  // and configured process lines move item lines); otherwise show the stored
  // Arena lines.
  const previewLines =
    isParentNew ||
    bomChanges.length > 0 ||
    (getCurrentItemFromPath?.process_changes?.length ?? 0) > 0;

  const tableRows = useMemo(() => {
    if (!getCurrentItemFromPath) {
      return filteredChildren.map((entry) => ({
        kind: "child" as const,
        key: `child-${entry.originalIndex}`,
        entry,
        lineNumber: entry.child.line_number ?? null,
        hint: entry.child.hint ?? null,
        isTentative: false,
      }));
    }
    const pendingSlotLines = new Set(
      Object.values(pendingSlotFills).map((slot) => slot.line_number)
    );
    return buildChildrenTableRows(getCurrentItemFromPath, filteredChildren, {
      isDeleted: isBomItemDeleted,
      preview: previewLines,
      includeSlot: (slot) =>
        !pendingSlotLines.has(slot.line_number) &&
        (slotMatchesSearch ? slotMatchesSearch(slot) : true),
    });
  }, [
    getCurrentItemFromPath,
    filteredChildren,
    isBomItemDeleted,
    previewLines,
    pendingSlotFills,
    slotMatchesSearch,
  ]);

  // FormSpec rejects a BOM listing the same part twice, so templates whose
  // base item is already a child (or a pending add) cannot be added again.
  const takenTemplateUuids = useMemo(() => {
    const taken = bomChildItemUuids(getCurrentItemFromPath);
    pendingItemUuids.forEach((itemUuid) => taken.add(itemUuid));
    return new Set(
      templates
        .filter(
          (template) =>
            template.base_item_uuid && taken.has(template.base_item_uuid)
        )
        .map((template) => template.uuid)
    );
  }, [getCurrentItemFromPath, pendingItemUuids, templates]);

  const templatesForSlot = useCallback(
    (slot: BomSlot) =>
      availableTemplates.filter(
        (template) =>
          templateToItemTypeMap.get(template.uuid) === slot.child_type_uuid
      ),
    [availableTemplates, templateToItemTypeMap]
  );

  return (
    <Section ref={sectionRef}>
      <SectionHeader>Children</SectionHeader>
      <ChildrenTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell sx={{ width: "70px" }}>Line #</TableHeadCell>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>Part Number</TableHeadCell>
              <TableHeadCell>Type</TableHeadCell>
              <TableHeadCell sx={{ width: "150px" }}>Quantity</TableHeadCell>
              <TableHeadCell sx={{ width: "60px" }}></TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length > 0 ? (
              tableRows.map((row) => {
                if (row.kind === "slot") {
                  return (
                    <SlotPlaceholderRow
                      key={row.key}
                      slot={row.slot}
                      templates={templatesForSlot(row.slot)}
                      disabledTemplateUuids={takenTemplateUuids}
                      isLoadingTemplates={isLoadingTemplates}
                      isPicking={fillingSlotLine === row.slot.line_number}
                      onStartFill={(slot) => {
                        setIsAddingNew(false);
                        setFillingSlotLine(slot.line_number);
                      }}
                      onCancelFill={() => setFillingSlotLine(null)}
                      onSelectTemplate={handleTemplateSelect}
                      readOnly={readOnly}
                    />
                  );
                }
                const { child, originalIndex } = row.entry;
                const deleteCheck = canDeleteBomItemAtIndex(originalIndex);
                return (
                  <ChildRow
                    key={originalIndex}
                    child={child}
                    lineNumber={row.lineNumber}
                    slotHint={row.hint}
                    isTentativeLine={row.isTentative}
                    currentPath={currentPath}
                    onItemClick={onItemClick}
                    onPartNumberReplace={onPartNumberReplace}
                    onDelete={handleDelete}
                    onUndo={handleUndo}
                    isBomItemDeleted={isBomItemDeleted}
                    isBomItemAdded={isBomItemAdded}
                    index={originalIndex}
                    canDelete={deleteCheck.canDelete}
                    deleteDisabledReason={deleteCheck.reason}
                    constraints={constraints}
                    parentItemUuid={uuid || null}
                    readOnly={readOnly}
                  />
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{ textAlign: "center", padding: "16px" }}
                >
                  <NoDataText
                    sx={{
                      backgroundColor: "transparent",
                      padding: 0,
                    }}
                  >
                    No children defined
                  </NoDataText>
                </TableCell>
              </TableRow>
            )}
            {isFetchingItems && pendingItemUuids.length > 0 && (
              <NewChildRow>
                <TableCell colSpan={6}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "16px",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </NewChildRow>
            )}
            {isAddingNew ? (
              <NewChildRow>
                <TableCell colSpan={6}>
                  <Box sx={{ paddingLeft: 1, paddingRight: 1 }}>
                    <Select
                      size="small"
                      value=""
                      onChange={(e) => {
                        const selectedUuid = e.target.value as string;
                        handleTemplateSelect(selectedUuid);
                      }}
                      displayEmpty
                      fullWidth
                      autoFocus
                      onBlur={() => setIsAddingNew(false)}
                      disabled={readOnly}
                      sx={{
                        fontSize: "14px",
                        backgroundColor: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#b0b0b0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select template...</em>
                      </MenuItem>
                      {isLoadingTemplates ? (
                        <MenuItem disabled>
                          <em>Loading templates...</em>
                        </MenuItem>
                      ) : (
                        availableTemplates.map((template) => {
                          const isTaken = takenTemplateUuids.has(template.uuid);
                          return (
                            <MenuItem
                              key={template.uuid}
                              value={template.uuid}
                              disabled={isTaken}
                            >
                              {template.name}
                              {isTaken && (
                                <em style={{ marginLeft: 8 }}>
                                  ({DUPLICATE_CHILD_MESSAGE})
                                </em>
                              )}
                            </MenuItem>
                          );
                        })
                      )}
                    </Select>
                  </Box>
                </TableCell>
              </NewChildRow>
            ) : (
              availableTemplates.length > 0 && (
                <AddTableRow
                  onClick={readOnly ? undefined : handleAddClick}
                  disabled={readOnly}
                >
                  <TableCell colSpan={6}>
                    <AddButton disabled={readOnly}>+</AddButton>
                  </TableCell>
                </AddTableRow>
              )
            )}
          </TableBody>
        </Table>
      </ChildrenTableContainer>
    </Section>
  );
};

export default ChildrenSection;
